var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { firstNameLastNameFromName } from "../../utils.mjs";
let stripePromise;
export const loadStripeIfNeeded = (cartPayment) => __awaiter(void 0, void 0, void 0, function* () {
    if (!stripePromise) {
        stripePromise = loadStripe(cartPayment);
    }
    return stripePromise;
});
const loadStripe = (cartPayment) => __awaiter(void 0, void 0, void 0, function* () {
    const stripeModule = yield import('@stripe/stripe-js');
    const stripe = yield stripeModule.loadStripe(cartPayment.apiKey);
    if (!stripe) {
        throw new Error('Failed to load Stripe');
    }
    return stripe;
});
export const getAddresses = (confirmEvent) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const name = firstNameLastNameFromName((_b = (_a = confirmEvent.billingDetails) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '');
    let address = {
        firstName: name.firstName,
        lastName: name.lastName,
        address1: (_d = (_c = confirmEvent.billingDetails) === null || _c === void 0 ? void 0 : _c.address.line1) !== null && _d !== void 0 ? _d : '',
        address2: (_f = (_e = confirmEvent.billingDetails) === null || _e === void 0 ? void 0 : _e.address.line2) !== null && _f !== void 0 ? _f : '',
        city: (_h = (_g = confirmEvent.billingDetails) === null || _g === void 0 ? void 0 : _g.address.city) !== null && _h !== void 0 ? _h : '',
        postalCode: (_k = (_j = confirmEvent.billingDetails) === null || _j === void 0 ? void 0 : _j.address.postal_code) !== null && _k !== void 0 ? _k : '',
        stateOrProvince: (_m = (_l = confirmEvent.billingDetails) === null || _l === void 0 ? void 0 : _l.address.state) !== null && _m !== void 0 ? _m : '',
        isValid: true, // We trust that Stripe has collected a valid address
    };
    let billingAddress = undefined;
    if (confirmEvent.shippingAddress) {
        billingAddress = address;
        const shippingName = firstNameLastNameFromName((_o = confirmEvent.shippingAddress.name) !== null && _o !== void 0 ? _o : '');
        address = {
            firstName: shippingName.firstName,
            lastName: shippingName.lastName,
            address1: (_p = confirmEvent.shippingAddress.address.line1) !== null && _p !== void 0 ? _p : '',
            address2: (_q = confirmEvent.shippingAddress.address.line2) !== null && _q !== void 0 ? _q : '',
            city: (_r = confirmEvent.shippingAddress.address.city) !== null && _r !== void 0 ? _r : '',
            postalCode: (_s = confirmEvent.shippingAddress.address.postal_code) !== null && _s !== void 0 ? _s : '',
            stateOrProvince: (_t = confirmEvent.shippingAddress.address.state) !== null && _t !== void 0 ? _t : '',
            isValid: true, // We trust that Stripe has collected a valid address
        };
    }
    return { address, billingAddress };
};
export const getShippingAddress = (details) => {
    var _a, _b, _c;
    return {
        firstName: (_a = details.value.firstName) !== null && _a !== void 0 ? _a : '',
        lastName: (_b = details.value.lastName) !== null && _b !== void 0 ? _b : '',
        address1: details.value.address.line1,
        address2: (_c = details.value.address.line2) !== null && _c !== void 0 ? _c : undefined,
        city: details.value.address.city,
        postalCode: details.value.address.postal_code,
        stateOrProvince: details.value.address.state,
        isValid: true,
    };
};
export const getBillingAddress = (details) => {
    return Object.assign(Object.assign({}, getShippingAddress(details)), { countryCode: details.value.address.country.toLowerCase() });
};
/**
 * Returns the appropriate Stripe locale for a given country code.
 * Defaults to *auto* if the country code is not found.
 */
export const getLocaleByCountryCode = (countryCode) => {
    return countryToLocale[countryCode.toUpperCase()] || 'auto';
};
const countryToLocale = {
    AU: 'en-AU',
    BR: 'pt-BR',
    BG: 'bg',
    CA: 'en-CA',
    CN: 'zh',
    HR: 'hr',
    CZ: 'cs',
    DK: 'da',
    EE: 'et',
    FI: 'fi',
    FR: 'fr-FR',
    DE: 'de',
    GR: 'el',
    HK: 'zh-HK',
    HU: 'hu',
    ID: 'id',
    IL: 'he',
    IT: 'it-IT',
    JP: 'ja',
    LV: 'lv',
    LT: 'lt',
    MY: 'ms',
    MT: 'mt',
    MX: 'es-419',
    NL: 'nl',
    NZ: 'en-NZ',
    NO: 'nb',
    PH: 'fil',
    PL: 'pl',
    PT: 'pt',
    RO: 'ro',
    RU: 'ru',
    SA: 'ar',
    SK: 'sk',
    SI: 'sl',
    KR: 'ko',
    ES: 'es-ES',
    SE: 'sv',
    TH: 'th',
    TR: 'tr',
    AE: 'ar',
    VN: 'vi',
};
/**
 * Country codes supported by Stripe Address Element.
 */
export const countryCodes = [
    'AF',
    'AX',
    'AL',
    'DZ',
    'AD',
    'AO',
    'AI',
    'AQ',
    'AG',
    'AR',
    'AM',
    'AW',
    'AC',
    'AU',
    'AT',
    'AZ',
    'BS',
    'BH',
    'BD',
    'BB',
    'BY',
    'BE',
    'BZ',
    'BJ',
    'BM',
    'BT',
    'BO',
    'BA',
    'BW',
    'BV',
    'BR',
    'IO',
    'VG',
    'BN',
    'BG',
    'BF',
    'BI',
    'KH',
    'CM',
    'CA',
    'CV',
    'BQ',
    'KY',
    'CF',
    'TD',
    'CL',
    'CN',
    'CO',
    'KM',
    'CG',
    'CD',
    'CK',
    'CR',
    'CI',
    'HR',
    'CW',
    'CY',
    'CZ',
    'DK',
    'DJ',
    'DM',
    'DO',
    'EC',
    'EG',
    'SV',
    'GQ',
    'ER',
    'EE',
    'SZ',
    'ET',
    'FK',
    'FO',
    'FJ',
    'FI',
    'FR',
    'GF',
    'PF',
    'TF',
    'GA',
    'GM',
    'GE',
    'DE',
    'GH',
    'GI',
    'GR',
    'GL',
    'GD',
    'GP',
    'GU',
    'GT',
    'GG',
    'GN',
    'GW',
    'GY',
    'HT',
    'HN',
    'HK',
    'HU',
    'IS',
    'IN',
    'ID',
    'IQ',
    'IE',
    'IM',
    'IL',
    'IT',
    'JM',
    'JP',
    'JE',
    'JO',
    'KZ',
    'KE',
    'KI',
    'XK',
    'KW',
    'KG',
    'LA',
    'LV',
    'LB',
    'LS',
    'LR',
    'LY',
    'LI',
    'LT',
    'LU',
    'MO',
    'MG',
    'MW',
    'MY',
    'MV',
    'ML',
    'MT',
    'MQ',
    'MR',
    'MU',
    'YT',
    'MX',
    'MD',
    'MC',
    'MN',
    'ME',
    'MS',
    'MA',
    'MZ',
    'MM',
    'NA',
    'NR',
    'NP',
    'NL',
    'NC',
    'NZ',
    'NI',
    'NE',
    'NG',
    'NU',
    'MK',
    'NO',
    'OM',
    'PK',
    'PS',
    'PA',
    'PG',
    'PY',
    'PE',
    'PH',
    'PN',
    'PL',
    'PT',
    'PR',
    'QA',
    'RE',
    'RO',
    'RU',
    'RW',
    'WS',
    'SM',
    'ST',
    'SA',
    'SN',
    'RS',
    'SC',
    'SL',
    'SG',
    'SX',
    'SK',
    'SI',
    'SB',
    'SO',
    'ZA',
    'GS',
    'KR',
    'SS',
    'ES',
    'LK',
    'BL',
    'SH',
    'KN',
    'LC',
    'MF',
    'PM',
    'VC',
    'SD',
    'SR',
    'SJ',
    'SE',
    'CH',
    'TW',
    'TJ',
    'TZ',
    'TH',
    'TL',
    'TG',
    'TK',
    'TO',
    'TT',
    'TA',
    'TN',
    'TR',
    'TM',
    'TC',
    'TV',
    'UG',
    'UA',
    'AE',
    'GB',
    'US',
    'UY',
    'UZ',
    'VU',
    'VA',
    'VE',
    'VN',
    'WF',
    'EH',
    'YE',
    'ZM',
    'ZW',
];
//# sourceMappingURL=utils.js.map